import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api } from "../lib/api";
import StatusPill from "../components/StatusPill";
import { BACKSTAGE_STATUS } from "../lib/constants";
import { formatLakhs } from "../lib/format";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

const EMPTY = { name: "", price_lakhs: 0, status: "open", assigned_to: "", notes: "" };

export default function BrandSpaces() {
  const [zones, setZones] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY);

  const reload = async () => {
    const { data } = await api.get("/backstage");
    setZones(data);
  };
  useEffect(() => { reload(); }, []);

  const totalRack = useMemo(() => zones.reduce((s, z) => s + z.price_lakhs, 0), [zones]);
  const sold = zones.filter((z) => z.status === "sold");
  const open = zones.filter((z) => z.status === "open");
  const negotiating = zones.filter((z) => z.status === "negotiating");
  const soldLakhs = sold.reduce((s, z) => s + z.price_lakhs, 0);

  const save = async () => {
    await api.put(`/backstage/${editingId}`, draft);
    setEditingId(null);
    await reload();
  };
  const create = async () => {
    if (!newRow.name.trim()) return;
    await api.post("/backstage", newRow);
    setNewRow(EMPTY); setAdding(false);
    await reload();
  };
  const remove = async (id) => {
    if (!confirm("Delete this zone?")) return;
    await api.delete(`/backstage/${id}`);
    await reload();
  };

  return (
    <Layout>
      <PageHeader
        overline="Backstage & artist proximity"
        title="Brand spaces"
        subtitle="High-touch zones outside the activation grid. Editorial, exclusive."
        actions={
          <button
            onClick={() => setAdding(true)}
            data-testid="add-zone-btn"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-4 py-2 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add zone
          </button>
        }
        testId="brandspaces-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="p-5" testId="kpi-zones-sold">
          <div className="overline mb-2">Zones sold</div>
          <div className="font-display text-3xl font-bold tabular">
            {sold.length}<span className="text-zinc-600 text-2xl"> / {zones.length}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">{negotiating.length} negotiating · {open.length} open</div>
        </Card>
        <Card className="p-5" testId="kpi-revenue-locked">
          <div className="overline mb-2">Revenue locked</div>
          <div className="font-display text-3xl font-bold tabular">{formatLakhs(soldLakhs)}</div>
          <div className="text-xs text-zinc-500 mt-1">of {formatLakhs(totalRack)} backstage rack</div>
        </Card>
        <Card className="p-5" testId="kpi-zones-open">
          <div className="overline mb-2">Zones open</div>
          <div className="font-display text-3xl font-bold tabular">{open.length}</div>
          <div className="text-xs text-zinc-500 mt-1">{formatLakhs(open.reduce((s, z) => s + z.price_lakhs, 0))} unsold value</div>
        </Card>
      </div>

      {/* Grid of zones */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {adding && (
          <Card className="p-5 border-emerald-500/40">
            <div className="overline mb-2">New zone</div>
            <input autoFocus value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
              placeholder="Zone name" data-testid="new-zone-name"
              className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm mb-2 focus:outline-none focus:border-emerald-500" />
            <div className="flex gap-2 mb-2">
              <input type="number" min={0} value={newRow.price_lakhs}
                onChange={(e) => setNewRow({ ...newRow, price_lakhs: +e.target.value })}
                placeholder="Price (₹L)"
                className="flex-1 bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              <select value={newRow.status} onChange={(e) => setNewRow({ ...newRow, status: e.target.value })}
                className="bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                data-testid="new-zone-status">
                {BACKSTAGE_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <input value={newRow.assigned_to} onChange={(e) => setNewRow({ ...newRow, assigned_to: e.target.value })}
              placeholder="Assigned brand"
              className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm mb-2 focus:outline-none focus:border-emerald-500" />
            <input value={newRow.notes} onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })}
              placeholder="Notes"
              className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-emerald-500" />
            <div className="flex items-center gap-2">
              <button onClick={create} data-testid="save-new-zone" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium py-2">Save</button>
              <button onClick={() => { setAdding(false); setNewRow(EMPTY); }} className="px-4 py-2 border border-zinc-800 text-sm text-zinc-300 hover:bg-[#181818]">Cancel</button>
            </div>
          </Card>
        )}
        {zones.map((z) => {
          const editing = editingId === z.id;
          return (
            <Card key={z.id} className="p-5 relative group" testId={`zone-${z.id}`}>
              {editing ? (
                <>
                  <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm mb-2 focus:outline-none focus:border-emerald-500" />
                  <div className="flex gap-2 mb-2">
                    <input type="number" min={0} value={draft.price_lakhs}
                      onChange={(e) => setDraft({ ...draft, price_lakhs: +e.target.value })}
                      className="flex-1 bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
                    <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                      className="bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500">
                      {BACKSTAGE_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <input value={draft.assigned_to} onChange={(e) => setDraft({ ...draft, assigned_to: e.target.value })}
                    placeholder="Assigned brand"
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm mb-2 focus:outline-none focus:border-emerald-500" />
                  <input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    placeholder="Notes"
                    className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm mb-3 focus:outline-none focus:border-emerald-500" />
                  <div className="flex items-center gap-2">
                    <button onClick={save} data-testid="zone-save" className="flex-1 inline-flex items-center justify-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium py-2">
                      <Check className="h-4 w-4" /> Save
                    </button>
                    <button onClick={() => setEditingId(null)} className="px-4 py-2 border border-zinc-800 text-sm text-zinc-300 hover:bg-[#181818]">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="font-display font-semibold text-lg leading-tight">{z.name}</h3>
                    <StatusPill status={z.status} />
                  </div>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="font-display text-3xl font-bold tabular">{formatLakhs(z.price_lakhs)}</span>
                  </div>
                  {z.assigned_to && (
                    <div className="text-sm mb-1">
                      <span className="overline mr-2">Assigned</span>
                      <span className="text-emerald-400">{z.assigned_to}</span>
                    </div>
                  )}
                  {z.notes && <div className="text-xs text-zinc-500 mt-2">{z.notes}</div>}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                    <button onClick={() => { setEditingId(z.id); setDraft({ ...z }); }} data-testid="zone-edit"
                      className="p-1.5 bg-[#181818] border border-zinc-800 text-zinc-300 hover:text-white">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(z.id)} data-testid="zone-delete"
                      className="p-1.5 bg-[#181818] border border-zinc-800 text-red-400 hover:text-red-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </>
              )}
            </Card>
          );
        })}
      </div>
    </Layout>
  );
}
